import React from 'react'
import clsx from 'clsx'
import useThunkDispatch from 'hooks/useThunkDispatch'
import { ReactComponent as HeartFilledSVG } from 'resources/graphics/heart_filled.svg'
import { setShouldRefetchLibrary } from 'store/services/Library'
import { setBookFavourite, BookMode, BookProgress } from 'store/services/Book'
import { lightenDarkenColor } from './utils'

import styles from './FavouriteButton.module.scss'

interface FavouriteButtonProps {
  onClick?: () => void
  isFavourite?: boolean
  bookProgress?: BookProgress
  disable?: boolean
  size?: 'small' | 'normal'
  bookMode?: BookMode
  isPink?: boolean
  className?: string
}

export const FavouriteButton: React.FC<FavouriteButtonProps> = ({
  isFavourite,
  onClick,
  className,
  bookProgress,
  disable,
  size = 'normal',
  bookMode,
  isPink
}) => {
  const dispatch = useThunkDispatch()

  const _isFavourite =
    isFavourite !== undefined
      ? isFavourite
      : bookMode === BookMode.READ
      ? !!bookProgress?.isFavorite?.reading
      : !!bookProgress?.isFavorite?.listening

  const _onClick = async () => {
    if (disable) return

    dispatch(setShouldRefetchLibrary(true))

    if (onClick) {
      onClick()
      return
    }

    if (bookProgress) {
      try {
        await dispatch(
          setBookFavourite(
            bookProgress._id,
            bookMode === BookMode.READ
              ? { reading: !bookProgress.isFavorite?.reading }
              : { listening: !bookProgress.isFavorite?.listening }
          )
        )
      } catch (error) {
        console.error(error)
      }
    }
  }

  const iconSize = size === 'small' ? 12 : 30

  const bgColor = _isFavourite ? '#f76988' : isPink ? '#FFBEA8' : '#c8c8c8'
  const shadowColor = lightenDarkenColor(bgColor, -25)

  return (
    <div className={clsx(styles.container, styles[size], className)}>
      <button
        type="button"
        aria-label="Favourite"
        className={clsx(styles.clickable, _isFavourite && styles.favorited, isPink && styles.pink)}
        onClick={_onClick}
        disabled={disable}
        style={{ backgroundColor: bgColor }}
      >
        <HeartFilledSVG width={iconSize} height={iconSize} fill={_isFavourite ? '#E80F62' : '#FFF2EE'} />
      </button>
      <div className={clsx(styles.bgShadow, styles[size])} style={{ backgroundColor: shadowColor }} />
    </div>
  )
}
